import { useState } from 'react'
import { Button, InputChips, InputText } from '@design-systems/canvas-ui'
import Icon, { PlusIcon, SearchIcon } from '@design-systems/react-icons'
import { useNavigate } from 'react-router-dom'
import BrandCard from '../components/BrandCard'
import BrandSetupModal from '../../brandSetup/components/BrandSetupModal'
import RoleGuard from '../../../shared/roleAccess/components/RoleGuard'
import {
  hasPermission,
  PERMISSIONS,
} from '../../../shared/roleAccess/config/roleConfig'
import { useBrandCatalog } from '../hooks/useBrandCatalog'
import { useCurrentUserRole } from '../../../shared/roleAccess/hooks/useCurrentUserRole'
import {
  BRAND_TYPES,
  MASTER_BRAND_CATEGORY,
} from '../../brandSetup/config/brandSetupConfig'
import { getBrandDetailsPath } from '../../../shared/routes/routes'
import './AllBrandsPage.css'

const AllBrandsPage = ({ onCreateBrand = () => {} }) => {
  const navigate = useNavigate()
  const [activeCategory, setActiveCategory] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [createModal, setCreateModal] = useState({
    brand: null,
    brandType: BRAND_TYPES.OWNED,
    isOpen: false,
    mode: 'create',
  })
  const role = useCurrentUserRole()
  const canViewBrands = hasPermission(role, PERMISSIONS.VIEW_BRANDS)
  const canEditBrands = hasPermission(role, PERMISSIONS.EDIT_BRAND)
  const {
    catalog,
    createBrand,
    error,
    isCreating,
    isLoading,
    isUpdating,
    updateBrand,
  } = useBrandCatalog({
    enabled: canViewBrands,
  })
  const populatedBrandGroups = (catalog?.ownedBrandGroups || []).filter(
    (group) => group.brands.length > 0,
  )
  const normalizedQuery = searchQuery.trim().toLowerCase()
  const matchesSearch = (brand) =>
    !normalizedQuery ||
    [brand.name, brand.primaryStewardName].some((value) =>
      value?.toLowerCase().includes(normalizedQuery),
    )
  const filteredBrandGroups = populatedBrandGroups
    .filter((group) => !activeCategory || group.id === activeCategory)
    .map((group) => ({
      ...group,
      brands: group.brands.filter(matchesSearch),
    }))
    .filter((group) => group.brands.length > 0)
  const visibleMasterBrands = !activeCategory
    ? (
        (catalog?.masterBrands?.length
          ? catalog.masterBrands
          : catalog?.masterBrand
            ? [catalog.masterBrand]
            : []) || []
      ).filter(matchesSearch)
    : []
  const hasVisibleBrands = Boolean(
    visibleMasterBrands.length || filteredBrandGroups.length,
  )
  const hasAnyBrands = Boolean(
    visibleMasterBrands.length ||
    catalog?.masterBrands?.length ||
    catalog?.masterBrand ||
    populatedBrandGroups.length,
  )
  const categoryOptions = populatedBrandGroups.map((group) => ({
    label: group.name,
    value: group.id,
  }))
  const allowedCreateBrandTypes = [BRAND_TYPES.OWNED, BRAND_TYPES.MASTER]
  const openCreateModal = (brandType = BRAND_TYPES.OWNED) => {
    onCreateBrand(brandType)
    setCreateModal({ brand: null, brandType, isOpen: true, mode: 'create' })
  }
  const openEditModal = (brand, category, brandType) =>
    setCreateModal({
      brand: { ...brand, brandType, category },
      brandType,
      isOpen: true,
      mode: 'edit',
    })

  return (
    <section className="brand-landing" aria-labelledby="brand-dna-heading">
      <h1 className="brand-landing__sr-only" id="brand-dna-heading">
        Brand Knowledge Base
      </h1>
      <div className="brand-landing__header">
        {canViewBrands && (
          <div className="brand-landing__lookup">
            <div className="brand-landing__search">
              <Icon size="md" svg={SearchIcon} />
              <InputText
                aria-label="Search brands"
                className="brand-landing__search-input"
                onChange={({ target: { value } }) => setSearchQuery(value)}
                placeholder="Search by brand, primary steward…"
                type="search"
                value={searchQuery}
              />
            </div>
            {categoryOptions.length > 0 && (
              <InputChips
                aria-label="Filter brands by category"
                className="brand-landing__category-filters"
                id="brand-category-filter"
                isSingleSelect
                onChange={(_id, value) => setActiveCategory(value || null)}
                options={categoryOptions}
                size="sm"
                value={activeCategory || undefined}
              />
            )}
          </div>
        )}
        <RoleGuard permission={PERMISSIONS.CREATE_BRAND}>
          <Button onClick={() => openCreateModal()} variant="primary">
            <span className="brand-landing__add-button-content">
              <Icon size="sm" svg={PlusIcon} />
              Add Brand
            </span>
          </Button>
        </RoleGuard>
      </div>

      {!canViewBrands && (
        <p className="brand-landing__error" role="alert">
          You do not have access to view Brand DNA content.
        </p>
      )}

      {canViewBrands && isLoading && (
        <p className="brand-landing__status" role="status">
          Loading brands…
        </p>
      )}

      {canViewBrands && error && (
        <p className="brand-landing__error" role="alert">
          We could not load the brand list. Please try again.
        </p>
      )}

      {canViewBrands && catalog && (
        <>
          {visibleMasterBrands.length > 0 && (
            <section
              className="brand-landing__section"
              aria-labelledby="master-brand-heading"
            >
              <h2 className="brand-landing__eyebrow" id="master-brand-heading">
                Master Brand
              </h2>
              <div className="brand-landing__grid">
                {visibleMasterBrands.map((brand) => (
                  <BrandCard
                    brand={brand}
                    key={brand.id}
                    onEdit={
                      canEditBrands
                        ? () =>
                            openEditModal(
                              brand,
                              MASTER_BRAND_CATEGORY,
                              BRAND_TYPES.MASTER,
                            )
                        : undefined
                    }
                    onOpen={() => navigate(getBrandDetailsPath(brand.slug))}
                  />
                ))}
              </div>
            </section>
          )}

          {filteredBrandGroups.length > 0 && (
            <section
              className="brand-landing__section"
              aria-labelledby="owned-brands-heading"
            >
              <h2 className="brand-landing__eyebrow" id="owned-brands-heading">
                Owned Brands
              </h2>
              {filteredBrandGroups.map((group) => (
                <section className="brand-landing__group" key={group.id}>
                  <h3 className="brand-landing__group-name">{group.name}</h3>
                  <div className="brand-landing__grid">
                    {group.brands.map((brand) => (
                      <BrandCard
                        brand={brand}
                        key={brand.id}
                        onEdit={
                          canEditBrands
                            ? () =>
                                openEditModal(
                                  brand,
                                  {
                                    id: group.id,
                                    label: group.name,
                                    value: group.id,
                                  },
                                  BRAND_TYPES.OWNED,
                                )
                            : undefined
                        }
                        onOpen={() =>
                          navigate(getBrandDetailsPath(brand.slug || brand.id))
                        }
                      />
                    ))}
                  </div>
                </section>
              ))}
            </section>
          )}
          {!hasAnyBrands && (
            <p className="brand-landing__status" role="status">
              No Brands available, please setup Target Master and Owned Brands.
            </p>
          )}
          {hasAnyBrands && !hasVisibleBrands && (
            <p className="brand-landing__status" role="status">
              No brands match your search or category filter.
            </p>
          )}
        </>
      )}

      <BrandSetupModal
        allowedBrandTypes={allowedCreateBrandTypes}
        initialBrand={createModal.brand}
        initialBrandType={createModal.brandType}
        isOpen={createModal.isOpen}
        isSubmitting={isCreating || isUpdating}
        key={`${createModal.mode}-${createModal.brand?.id || createModal.brandType}-${createModal.isOpen}`}
        mode={createModal.mode}
        onClose={() =>
          setCreateModal((current) => ({ ...current, isOpen: false }))
        }
        onSubmit={(form) =>
          createModal.mode === 'edit'
            ? updateBrand(form, createModal.brand)
            : createBrand(form)
        }
      />
    </section>
  )
}

export default AllBrandsPage
